import type { ReactElement, ReactNode } from 'react';
import { createRef, useCallback, useState } from 'react';
import { Button } from '@fastnorth/react-cerberus/Button';
import { Dialog } from '@fastnorth/react-cerberus/Dialog';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client';
import type { ReportInput, ReportPhotoMutationVariables, ReportPhotoResponseData } from './queries/sendReport';
import { SEND_REPORT_MUTATION } from './queries/sendReport';
import ReportForm from './ReportForm';
import ReportFormResult from './ReportFormResult';
import Loading from '../../../loading/Loading';
import deriveRecaptchaKey from './utils/deriveRecaptchaKey';

type ReportDialogType = {
  photoId: string;
  galleryId?: string;
};

type ConditionalRender = {
  form: () => ReactNode;
  result: () => ReactNode;
};

const serializer =
  (recaptchaToken = '') =>
  (body: any, headers: Headers): any => {
    headers.set('Content-Type', 'application/json');
    headers.set('X-Recaptcha-Token', recaptchaToken);
    return {
      body: JSON.stringify(body),
      headers,
    };
  };

const ReportDialog = ({ photoId, galleryId }: ReportDialogType): ReactElement | null => {
  const formRef = createRef<HTMLFormElement>();
  const { t } = useTranslation();
  const [recaptchaToken, setRecaptchaToken] = useState<string>();
  const [recaptchaError, setRecaptchaError] = useState<boolean>(false);
  const [submitted, setSubmitted] = useState<boolean>(false);
  const [input, setInput] = useState<ReportInput>({ details: undefined, reason: undefined });
  const recaptchaSiteKey = deriveRecaptchaKey();

  const [sendReport, { loading: reporting, error, reset }] = useMutation<
    ReportPhotoResponseData,
    ReportPhotoMutationVariables
  >(SEND_REPORT_MUTATION, {
    variables: {
      galleryId,
      photoId,
      input,
      serializer: serializer(recaptchaToken),
    },
    update: (cache) => {
      cache.evict({ id: photoId, broadcast: true });
      cache.gc();
    },
  });

  const onOpenChange = (open: boolean): void => {
    if (!open) {
      setRecaptchaToken(undefined);
      setSubmitted(false);
      reset();
      return;
    }
  };

  const onSubmit = useCallback((): void => {
    setRecaptchaError(!recaptchaToken);
    if (formRef && formRef.current && formRef.current.reportValidity() && recaptchaToken) {
      sendReport().then(() => setSubmitted(true));
    }
  }, [formRef, recaptchaToken, sendReport]);

  if (!recaptchaSiteKey) {
    return null;
  }

  const CONDITIONAL_RENDER: ConditionalRender = {
    form: () => (
      <ReportForm
        ref={formRef}
        setInput={setInput}
        setRecaptchaToken={setRecaptchaToken}
        recaptchaError={recaptchaError}
        setRecaptchaError={setRecaptchaError}
      />
    ),
    result: () => <ReportFormResult isSubmitted={submitted} />,
  };

  const blockToRender = submitted || !!error ? 'result' : 'form';

  return (
    <Dialog
      onOpenChange={onOpenChange}
      modal={false} // Needs to run in non-modal mode to allow for the recaptcha to work
    >
      <Dialog.Trigger>
        <Button
          className={'text-nowrap'}
          variant={'unstyled'}
          iconLeft={'exclamation-circle'}
          text={`${t('photos_ui.photo_modal.report_photo')}`}
        />
      </Dialog.Trigger>
      <Dialog.Content
        onInteractOutside={(e) => e.preventDefault()}
        title={<h4>{t('photos_ui.report.title')}</h4>}
        footer={
          <>
            <Dialog.Close>
              <Button variant="secondary" disabled={reporting}>
                {error || submitted ? t('photos_ui.report.button.close') : t('photos_ui.report.button.cancel')}
              </Button>
            </Dialog.Close>
            {!(error || submitted) && (
              <Dialog.Close>
                <Button variant="success" onClick={onSubmit}>
                  {t('photos_ui.report.button.submit')}
                </Button>
              </Dialog.Close>
            )}
          </>
        }>
        <Loading busy={reporting}>{CONDITIONAL_RENDER[blockToRender]()}</Loading>
      </Dialog.Content>
    </Dialog>
  );
};

export default ReportDialog;
